import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

type Phase = "idle" | "hidden" | "shown";

/**
 * Reveals a block the first time it scrolls into view. The server renders the
 * block with no reveal classes at all, so crawlers and visitors without
 * JavaScript always get the finished text; the hidden state is only applied
 * once the component is live in the browser.
 */
function useInView<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setPhase("shown");
      return;
    }
    setPhase("hidden");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPhase("shown");
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.02 },
    );
    observer.observe(el);
    // Safety net: text must never stay hidden if the observer never fires.
    const safety = window.setTimeout(() => setPhase("shown"), 3500);
    return () => {
      observer.disconnect();
      window.clearTimeout(safety);
    };
  }, []);

  return { ref, phase };
}

type RevealProps = {
  /** Element or component to render. Defaults to a div. */
  as?: ElementType;
  /** Stagger in milliseconds, used for single blocks. */
  delay?: number;
  className?: string;
  children?: ReactNode;
  [key: string]: unknown;
};

function classesFor(phase: Phase, base: string, className: string) {
  return [
    phase === "idle" ? "" : base,
    phase === "shown" ? "is-in" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

/** Fades and raises one block into place when it reaches the viewport. */
export function Reveal({
  as,
  delay = 0,
  className = "",
  children,
  ...rest
}: RevealProps) {
  // Cast keeps the polymorphic `as` prop simple; any element may be rendered.
  const Tag = (as ?? "div") as any;
  const { ref, phase } = useInView<HTMLElement>();
  return (
    <Tag
      ref={ref}
      className={classesFor(phase, "reveal", className)}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Reveals a container's direct children one after another, so a grid of cards
 * or a list of points settles in rather than appearing all at once.
 */
export function RevealGroup({
  as,
  className = "",
  children,
  ...rest
}: RevealProps) {
  const Tag = (as ?? "div") as any;
  const { ref, phase } = useInView<HTMLElement>();
  return (
    <Tag ref={ref} className={classesFor(phase, "reveal-group", className)} {...rest}>
      {children}
    </Tag>
  );
}
